import React, { useMemo } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { StaticDatePicker } from '@mui/x-date-pickers';
import TextField from '@mui/material/TextField';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarRange } from '@fortawesome/pro-light-svg-icons';
import { formattedDate } from 'shared/utils/date';
import { CHARITY_NAVIGATOR_MAX_START_DATE } from './utils';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

interface CustomPeriodPickerProps {
  startDate?: Date;
  endDate?: Date;
  disableFuture: boolean;
  handleStartDate: (date: Date | null) => unknown;
  handleEndDate: (date: Date | null) => unknown;
}

const border = `border: 1px solid ${COLORS.grayLight}`;
const StyledDiv = styled.div<{
  isEndDateValid: boolean;
  startAndEndDateIsSame: boolean;
}>`
  display: flex;
  margin-top: 40px;
  justify-content: center;
  .left-row {
    margin-right: 24px;
    .start-date-error {
      visibility: hidden;
    }
  }
  .right-row {
    margin-left: 24px;
    .end-date {
      ${({ isEndDateValid }) =>
        isEndDateValid
          ? ''
          : `color: ${COLORS.critical}; border-color: ${COLORS.critical};`}
    }
    .end-date-error {
      visibility: ${({ isEndDateValid, startAndEndDateIsSame }) =>
        isEndDateValid && !startAndEndDateIsSame ? 'hidden' : 'visible'};
    }
    .MuiPickersDay-root.Mui-selected {
      ${({ isEndDateValid }) =>
        isEndDateValid
          ? ''
          : `background-color: ${COLORS.critical} !important;`}
    }
  }
  .MuiPickersDay-root.Mui-selected,
  .MuiPickersDay-root.Mui-selected:hover,
  .MuiPickersDay-root.Mui-selected:focus {
    background-color: ${COLORS.ctaPrimary} !important;
    color: ${COLORS.whiteBase} !important;
  }
  .start-date-label,
  .end-date-label {
    text-align: left;
    margin-bottom: 11px;
  }
  .start-date,
  .end-date {
    ${border};
    border-radius: 4px;
    padding: 12px 16px;
    svg {
      margin-right: 8px;
      path {
        fill: ${COLORS.ctaPrimary};
      }
    }
  }
  .MuiPickersStaticWrapper-staticWrapperRoot {
    ${border};
    padding-top: 20px;
    border-radius: 8px;
    margin-top: 5px;
    .MuiIconButton-root {
      border-radius: 8px;
    }
    .MuiPickersCalendarHeader-iconButton {
      ${border};
    }
    .MuiPickersCalendarHeader-transitionContainer {
      width: 63%;
    }
    .MuiPickersCalendarHeader-switchHeader {
      justify-content: center;
    }
  }
  @media only screen and (max-width: 1060px) {
    flex-direction: column;
    .left-row {
      margin-right: 0;
    }
    .right-row {
      margin-top: 16px;
      margin-left: 0;
    }
    .MuiPickersBasePicker-pickerView {
      min-width: 100%;
    }
  }
`;

const CustomPeriodPicker: React.FC<CustomPeriodPickerProps> = ({
  startDate,
  endDate,
  disableFuture,
  handleStartDate,
  handleEndDate,
}) => {
  const { isCNUser } = useUserActions();
  const minEndDate = startDate && new Date(startDate);
  minEndDate?.setDate(minEndDate.getDate() + 1);

  const minStartDate = useMemo(() => {
    if (!isCNUser) return undefined;
    const date = new Date();
    date.setDate(date.getDate() - CHARITY_NAVIGATOR_MAX_START_DATE);
    return date;
  }, [isCNUser]);

  const isEndDateValid = startDate && endDate ? endDate >= startDate : true;
  const startAndEndDateIsSame = Boolean(
    startDate && endDate && startDate.getTime() === endDate.getTime(),
  );

  return (
    <StyledDiv
      isEndDateValid={isEndDateValid}
      startAndEndDateIsSame={startAndEndDateIsSame}
    >
      <div className="left-row show-cn-calendar">
        <TrueImpactText variant="body-bold" className="start-date-label">
          Start date
        </TrueImpactText>
        <TrueImpactText variant="body" className="start-date">
          <FontAwesomeIcon icon={faCalendarRange} />{' '}
          {formattedDate(startDate?.toDateString())}
        </TrueImpactText>
        <TrueImpactText
          variant="body-small"
          className="start-date-error"
          color="critical"
        >
          Start date is not valid
        </TrueImpactText>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          disableFuture={disableFuture}
          minDate={minStartDate}
          inputFormat="dd MMM yyyy"
          value={startDate}
          onChange={handleStartDate}
          renderInput={params => <TextField {...params} />}
        />
      </div>
      <div className="right-row show-cn-calendar">
        <TrueImpactText variant="body-bold" className="end-date-label">
          End date
        </TrueImpactText>
        <TrueImpactText variant="body" className="end-date">
          <FontAwesomeIcon icon={faCalendarRange} />{' '}
          {formattedDate(endDate?.toDateString())}
        </TrueImpactText>
        <TrueImpactText
          variant="body-small"
          className="end-date-error"
          color={startAndEndDateIsSame ? 'warning' : 'critical'}
        >
          {startAndEndDateIsSame
            ? 'Warning: End date is the same as the start date'
            : 'End date cannot be prior to the start date'}
        </TrueImpactText>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          disableFuture={disableFuture}
          minDate={minStartDate}
          inputFormat="dd MMM yyyy"
          // minDate={minEndDate}
          value={endDate}
          onChange={handleEndDate}
          renderInput={params => <TextField {...params} />}
        />
      </div>
    </StyledDiv>
  );
};

export default CustomPeriodPicker;
